import { useState, useEffect, useMemo } from "react";
import * as satellite from "satellite.js";
import { SatelliteTleData } from "../../SatDataLoader/types";
import { normalizeTleExponents } from "../../utils/normalizeTleExponents";
import { parseTleLines } from "../../utils/parseTleLines";
import { startVisibleInterval } from "../../utils/startVisibleInterval";
import { generateFutureOrbit, PathPoint } from "../utils/orbitGeometry";

export type PathData = {
  kind: "orbit";
  satId: string;
  name: string;
  points: PathPoint[];
};

export const useOrbitTrails = (
  satTleData: SatelliteTleData[],
  showSatTrails: boolean,
  simulationTime: number | null = null,
  endTime: number | null = null,
) => {
  const [liveTick, refresh] = useState(0);
  const simulating = simulationTime !== null;
  const sources = useMemo(
    () =>
      satTleData.flatMap((sat) => {
        const lines = parseTleLines(sat.tle);
        if (!lines) return [];
        return [
          {
            satrec: satellite.twoline2satrec(
              normalizeTleExponents(lines[0]),
              lines[1],
            ),
            path: {
              kind: "orbit" as const,
              satId: String(sat.info.satid),
              name: `${sat.info.satname} · next 45 minutes`,
              points: [] as PathPoint[],
            },
          },
        ];
      }),
    [satTleData],
  );

  useEffect(() => {
    if (!showSatTrails || simulating) return;
    return startVisibleInterval(() => refresh((tick) => tick + 1), 10000);
  }, [showSatTrails, simulating]);

  const orbitPathData = useMemo(() => {
    if (!showSatTrails) return [];
    const time = simulationTime ?? Date.now();
    const durationMinutes =
      endTime === null ? 45 : Math.min(45, (endTime - time) / 60000);
    return sources.flatMap(({ satrec, path }) => {
      try {
        path.points = generateFutureOrbit(satrec, time, durationMinutes);
        return path.points.length > 1 ? [path] : [];
      } catch {
        return [];
      }
    });
    // A visible-page tick refreshes the live forecast without a second clock in simulation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, showSatTrails, simulationTime, endTime, liveTick]);

  return { orbitPathData };
};
